import { getStartedSection, prerequisites, steps } from '@/content/site'

import { Reveal } from './shared/Reveal'
import { SplitTitle } from './shared/SplitTitle'

/**
 * The onboarding path, from docs-public/getting-started/quickstart.md.
 *
 * There is no public dashboard to send anyone to yet, so this section
 * describes the flow rather than driving a signup. When `DASHBOARD_URL` is
 * set in content/site.ts, restore a primary CTA here.
 */
export function GetStartedSection() {
  return (
    <section id="get-started" className="border-t border-hairline py-16 md:py-24">
      <div className="shell">
        <Reveal>
          <p className="eyebrow">{getStartedSection.eyebrow}</p>
        </Reveal>

        <h2 className="max-w-2xl font-sans text-[1.6rem] md:text-[2.15rem] font-bold tracking-[-0.02em] leading-[1.16]">
          <SplitTitle text={getStartedSection.title} />
        </h2>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-14">
          {/* Steps */}
          <ol className="space-y-3">
            {steps.map((step, i) => (
              <Reveal key={step.n} i={i} as="li">
                <div className="gs-step grid grid-cols-[2.5rem_1fr] gap-3 rounded-lg p-4">
                  <span className="gs-step__num font-mono text-xs tracking-widest text-accent">
                    {step.n}
                  </span>
                  <div>
                    <h3 className="gs-step__title font-sans text-base font-semibold">
                      {step.title}
                    </h3>
                    <p className="mt-1 font-paragraph text-xs leading-relaxed text-ink-dim md:text-[0.82rem]">
                      {step.body}
                    </p>
                    <p className="mt-2 font-mono text-[0.63rem] uppercase tracking-[0.18em] text-ink-mute">
                      {step.detail}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </ol>

          {/* Prerequisites + what the import actually does */}
          <Reveal i={1}>
            <div className="flex h-full flex-col rounded-xl border border-hairline bg-bg-elev p-6 md:p-8">
              <p className="micro-label">{getStartedSection.beforeLabel}</p>
              <ul className="mt-4 space-y-3">
                {prerequisites.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                      className="mt-0.5 flex-shrink-0 text-node-output"
                    >
                      <path
                        d="M20 6L9 17l-5-5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="font-paragraph text-sm leading-relaxed text-ink-dim">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="rule-fade my-6" />

              <p className="micro-label">{getStartedSection.importLabel}</p>
              <ol className="mt-4 space-y-2">
                {getStartedSection.importSteps.map((line, i) => (
                  <li key={line} className="flex items-baseline gap-3">
                    <span className="font-mono text-[0.63rem] tracking-[0.16em] text-accent/70">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="font-paragraph text-xs leading-relaxed text-ink-dim md:text-[0.82rem]">
                      {line}
                    </span>
                  </li>
                ))}
              </ol>

              {/* mt-auto keeps the note pinned to the bottom of the card */}
              <p className="mt-auto pt-6 font-paragraph text-xs leading-relaxed text-ink-mute">
                {getStartedSection.rolloutNote}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}
